import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { LogIn, Store, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLogin = () => {
  const { storeDomain, adminPath } = useParams();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [storeError, setStoreError] = useState(null);
  const [loadingStore, setLoadingStore] = useState(true);
  const [resolvedDomain, setResolvedDomain] = useState(null);
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (storeDomain) {
      loadStoreInfoByDomain(storeDomain);
    } else if (adminPath) {
      loadStoreInfoByAdminPath(adminPath);
    } else {
      setLoadingStore(false);
    }
  }, [storeDomain, adminPath]);

  const loadStoreInfoByDomain = async (domain) => {
    try {
      const response = await axios.get(`${API}/stores/domain/${domain}`);
      setStoreInfo(response.data);
      setResolvedDomain(domain);
      setStoreError(null);
    } catch (error) {
      setStoreError('Tienda no encontrada');
      setStoreInfo(null);
    } finally {
      setLoadingStore(false);
    }
  };

  const loadStoreInfoByAdminPath = async (path) => {
    try {
      const response = await axios.get(`${API}/stores/by-admin-path/${path}`);
      setStoreInfo(response.data);
      setResolvedDomain(response.data.domain);
      setStoreError(null);
    } catch (error) {
      setStoreError('Ruta de administración no encontrada');
      setStoreInfo(null);
    } finally {
      setLoadingStore(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(username, password, resolvedDomain || null);
    
    if (result.success) {
      toast.success('Inicio de sesión exitoso');
      // Navigate using the custom admin path if available
      const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : '/admin');
      navigate(`${basePath}/dashboard`);
    } else {
      toast.error(result.error || 'Usuario o contraseña incorrectos');
    }
    
    setLoading(false);
  };

  if (loadingStore) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <Store size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  if (storeError) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <div className="bg-white rounded-xl shadow-lg p-8 border text-center max-w-md" style={{ borderColor: 'var(--border-light)' }}>
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h1 className="heading-3 mb-2" data-testid="store-not-found-title">Acceso denegado</h1>
          <p className="text-text-muted mb-6" data-testid="store-not-found-message">
            La ruta "<strong>{adminPath || storeDomain}</strong>" no existe o no está autorizada.
          </p>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
            data-testid="go-home-btn"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg p-8 border" style={{ borderColor: 'var(--border-light)' }}>
          {/* Logo */}
          <div className="flex items-center justify-center gap-2 mb-2">
            <Store className="text-accent-text" size={36} />
            <h1 className="heading-2" data-testid="admin-login-title">Admin Panel</h1>
          </div>
          
          {/* Store Name */}
          {storeInfo && (
            <p className="text-center text-accent-text font-medium mb-6" data-testid="store-name-label">
              {storeInfo.store_name}
            </p>
          )}
          {!storeDomain && !adminPath && (
            <p className="text-center text-text-muted text-sm mb-6">
              Acceso general
            </p>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Usuario
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="login-username-input"
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: 'var(--border-light)',
                  '--tw-ring-color': 'var(--accent-primary)'
                }}
                placeholder="admin"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="login-password-input"
                className="w-full px-4 py-3 rounded-lg border focus:outline-none focus:ring-2 transition-all"
                style={{
                  borderColor: 'var(--border-light)',
                  '--tw-ring-color': 'var(--accent-primary)'
                }}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="btn-primary w-full"
              style={{ opacity: loading ? 0.6 : 1 }}
            >
              <LogIn size={20} className="inline mr-2" />
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </button>
          </form>

          {/* Info */}
          <div className="mt-6 text-center">
            <p className="text-sm text-text-muted">
              {storeInfo ? `Administración de ${storeInfo.store_name}` : 'Panel de administración'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
